<template>
  <div>
    <div class="flex flex-col gap-32">
      <RecommendationAlgoliaCarousel
        v-if="isAlgoliaRecommendationBoughtTogetherActive"
        :recommendation-query="{
          objectID: algoliaObjectId,
          model: 'bought-together',
          options: {
            maxRecommendations: 12,
            queryParameters: {
              ruleContexts: ['only_protection'],
            },
            fallbackParameters: {
              ruleContexts: ['only_protection'],
            },
          },
        }"
        :show-success-toast="false"
        :title="i18n(translations.recommendationsTitle)"
        :trackingData="{
          list: 'cross sell modal 2',
        }"
        :withAddToCart="true"
        @added-to-cart="(product: Product) => emit('added-to-cart', product)"
        @error="() => carouselStatuses.push('error')"
        @loaded="() => carouselStatuses.push('loaded')"
      />
      <RecommendationCarousel
        v-else
        :options="{
          withCrossedPrice: true,
          withStartingFrom: false,
          withGrade: true,
          withAddToCart: true,
        }"
        :recommendation-query="{
          category: 'crossSellProtection',
          scope: 'addToCart',
          scopeId: `${listingId}`,
          limit: 12,
        }"
        :show-success-toast="false"
        :title="i18n(translations.recommendationsTitle)"
        :trackingData="{
          list: 'cross sell modal 2',
        }"
        @added="(product: Product) => emit('added-to-cart', product)"
        @error="() => carouselStatuses.push('error')"
        @loaded="() => carouselStatuses.push('loaded')"
      />

      <RecommendationAlgoliaCarousel
        v-if="isAlgoliaRecommendationBoughtTogetherActive"
        :recommendation-query="{
          objectID: algoliaObjectId,
          model: 'bought-together',
          options: {
            maxRecommendations: 12,
            queryParameters: {
              ruleContexts: ['without_protection'],
            },
            fallbackParameters: {
              ruleContexts: ['without_protection'],
            },
          },
        }"
        :show-success-toast="false"
        :title="i18n(translations.crossSellTitle)"
        :trackingData="{
          list: 'cross sell modal 1',
        }"
        :withAddToCart="true"
        @added-to-cart="(product: Product) => emit('added-to-cart', product)"
        @error="() => carouselStatuses.push('error')"
        @loaded="() => carouselStatuses.push('loaded')"
      />
      <RecommendationCarousel
        v-else
        :options="{
          withCrossedPrice: true,
          withStartingFrom: false,
          withGrade: true,
          withAddToCart: true,
        }"
        :recommendation-query="{
          category: 'crossSellTwo',
          scope: 'addToCart',
          scopeId: `${listingId}`,
          limit: 12,
        }"
        :show-success-toast="false"
        :title="i18n(translations.crossSellTitle)"
        :trackingData="{
          list: 'cross sell modal 1',
        }"
        @added="(product: Product) => emit('added-to-cart', product)"
        @error="() => carouselStatuses.push('error')"
        @loaded="() => carouselStatuses.push('loaded')"
      />
    </div>

    <RevStickyBar class="flex justify-between px-24 py-12">
      <RevLink data-qa="continue-shopping" @click="handleBackRedirect">
        {{ i18n(translations.back) }}
      </RevLink>
      <RevButton
        data-qa="go-to-cart"
        variant="primary"
        @click="handleAddToCartRedirect"
      >
        {{ i18n(translations.continue) }}
      </RevButton>
    </RevStickyBar>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'

import RecommendationAlgoliaCarousel from '@backmarket/nuxt-layer-recommendation/RecommendationAlgoliaCarousel.vue'
import RecommendationCarousel from '@backmarket/nuxt-layer-recommendation/RecommendationCarousel.vue'
import type { Product } from '@backmarket/nuxt-layer-recommendation/models/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevLink } from '@ds/components/Link'
import { RevStickyBar } from '@ds/components/StickyBar'

import { useProductUrlToAlgoliaObjectId } from '~/scopes/product/composables/useProductUrlToAlgoliaObjectId'

import translations from './Recommendations.translations'

const i18n = useI18n()

defineProps<{
  listingId: number | string
}>()

const emit = defineEmits([
  'added-to-cart',
  'loaded',
  'no-recommendation',
  'back',
  'go-to-cart',
])
const carouselStatuses = ref<Array<'error' | 'loaded'>>([])
const { trackClick } = useTracking()
const experiments = useExperiments()
const isAlgoliaRecommendationBoughtTogetherActive =
  experiments['experiment.algoliaRecommendBoughtTogether'] === 'Algolia'
const algoliaObjectId = useProductUrlToAlgoliaObjectId()

watch([carouselStatuses.value], () => {
  if (carouselStatuses.value && carouselStatuses.value.length === 2) {
    if (carouselStatuses.value.every((value) => value === 'error')) {
      emit('no-recommendation')
    } else {
      emit('loaded')
    }
  }
})

async function handleBackRedirect() {
  trackClick({
    name: 'continue_shopping',
    zone: 'add_to_cart_modal',
  })

  emit('back')
}

async function handleAddToCartRedirect() {
  trackClick({
    name: 'go_to_cart',
    zone: 'add_to_cart_modal',
  })

  emit('go-to-cart')
}
</script>
